import React from 'react';
import { Camera, Target, Plus, ChevronRight, Zap, TrendingUp, Award, X, RefreshCw, Sparkles, PlayCircle, Moon, Clock, Sliders, Repeat, MoreVertical, Trash2 } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { resolveDailyDrills, calculateSessionDuration, adjustDrillIntensity, getAlternativeDrill, MASTER_DRILL_DB } from '../../utils/planner';
import { doc, updateDoc, getDoc, setDoc, collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import DrillCreator from './DrillCreator';
import logger from '../../utils/logger';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler);

const DrillLibraryView = ({ onAddDrill, onCreateDrill, mergedDrills = MASTER_DRILL_DB }) => {
    const [activeTab, setActiveTab] = React.useState('Batting'); // Batting, Bowling, Fielding, Fitness, Mental
    const [activeCategory, setActiveCategory] = React.useState('All');

    // Filter Logic
    const filteredDrills = mergedDrills.filter(drill => {
        if (drill.role_focus !== activeTab) return false;
        if (activeCategory !== 'All' && drill.category !== activeCategory) return false;
        return true;
    });

    // Extract unique categories for the current tab
    const categories = ['All', ...new Set(mergedDrills.filter(d => d.role_focus === activeTab).map(d => d.category))];

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Top Level Tabs */}
            <div className="flex overflow-x-auto border-b border-gray-800 bg-gray-900/50">
                {['Batting', 'Bowling', 'Fielding', 'Fitness', 'Mental'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => { setActiveTab(tab); setActiveCategory('All'); }}
                        className={`px-4 py-3 text-sm font-bold uppercase tracking-wider whitespace-nowrap transition-colors border-b-2 ${activeTab === tab ? 'text-white border-purple-500' : 'text-gray-500 border-transparent hover:text-gray-300'}`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Sub-Category Filters */}
            <div className="p-3 bg-gray-900 border-b border-gray-800 overflow-x-auto flex gap-2 no-scrollbar">
                {categories.map(cat => (
                    <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap border transition-all ${activeCategory === cat
                            ? 'bg-purple-600 text-white border-purple-600 shadow-lg shadow-purple-900/40'
                            : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-500'}`}
                    >
                        {cat}
                    </button>
                ))}
            </div>

            {/* Drill List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 pb-20">
                {/* Create Custom Drill CTA */}
                <button
                    onClick={onCreateDrill}
                    className="w-full p-4 mb-4 rounded-xl border border-dashed border-purple-500/50 bg-purple-500/5 hover:bg-purple-500/10 flex items-center justify-center gap-2 group transition-all"
                >
                    <Plus size={18} className="text-purple-400" />
                    <span className="text-sm font-bold text-purple-300 uppercase tracking-wider">Create Your Own Drill</span>
                </button>

                {filteredDrills.length > 0 ? (
                    filteredDrills.map(drill => (
                        <button
                            key={`lib-${drill.drill_id}`}
                            onClick={() => onAddDrill(drill)}
                            className="w-full p-4 rounded-xl border border-gray-800 bg-gray-800/20 hover:bg-gray-800 hover:border-purple-500/50 text-left transition-all group relative overflow-hidden"
                        >
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[10px] uppercase font-bold text-purple-400 tracking-wider">
                                            {drill.isUserCreated ? 'Custom' : drill.category}
                                        </span>
                                        {drill.tags?.includes('Solo') && <span className="text-[10px] bg-gray-700 text-gray-300 px-1.5 rounded">Solo</span>}
                                        {drill.tags?.includes('Partner') && <span className="text-[10px] bg-blue-900/30 text-blue-300 px-1.5 rounded">Partner</span>}
                                    </div>
                                    <span className="block text-base font-bold text-white mb-1 group-hover:text-purple-300 transition-colors">{drill.name}</span>

                                    {/* Mini Tags */}
                                    <div className="flex flex-wrap gap-1 opacity-60">
                                        {drill.tags?.slice(1).map(tag => (
                                            <span key={tag} className="text-[10px] text-gray-400">#{tag}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="bg-gray-800 p-2 rounded-full group-hover:bg-purple-600 transition-colors shadow-lg text-gray-400 group-hover:text-white">
                                    <Plus size={16} />
                                </div>
                            </div>
                        </button>
                    ))
                ) : (
                    <div className="text-center py-10 text-gray-500">
                        <p>No drills found in this category.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const Dashboard = ({ user, onLogActivity, onStartSession }) => {
    const { info } = useToast();
    const profile = user?.profile || {};
    // Fix: Read stats and grindScore from the profile object, not the user object
    const stats = profile?.stats || { matchesPlayed: 0, trainingSessions: 0 };
    const grindScore = profile?.grindScore || 50;
    const totalXP = profile?.totalXp || 0;

    // Mock Graph Data (would normally come from history)
    const chartData = {
        labels: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
        datasets: [
            {
                fill: true,
                data: [45, 48, 47, 52, 50, 55, grindScore],
                borderColor: 'rgb(147, 51, 234)',
                backgroundColor: 'rgba(147, 51, 234, 0.1)',
                tension: 0.4,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        plugins: { legend: { display: false } },
        scales: {
            x: { display: false },
            y: { display: false, min: 0, max: 100 }
        },
        elements: { point: { radius: 0 } }
    };

    // --- State Management ---
    const [selectedDay, setSelectedDay] = React.useState(null);
    const [dayDrills, setDayDrills] = React.useState([]);
    const [intensity, setIntensity] = React.useState('medium'); // low, medium, high
    const [isTimeCrunched, setIsTimeCrunched] = React.useState(false);

    // Drill Library Modal State
    const [showDrillLibrary, setShowDrillLibrary] = React.useState(false);
    const [showDrillCreator, setShowDrillCreator] = React.useState(false);

    // Custom Drills State
    const [customDrills, setCustomDrills] = React.useState([]);

    React.useEffect(() => {
        if (!user?.uid) return;

        const q = query(collection(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.CUSTOM_DRILLS));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const drills = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCustomDrills(drills);
        });

        return () => unsubscribe();
    }, [user?.uid]);

    const mergedDrills = [...MASTER_DRILL_DB, ...customDrills];

    const handleDayClick = (day, index) => {
        const drills = resolveDailyDrills(day, profile, customDrills);
        setSelectedDay({ ...day, index });
        setDayDrills(drills);
        // Reset controls
        setIntensity('medium');
        setIsTimeCrunched(false);
    };

    // Level 1: Rest Day
    const handleTakeRestDay = async () => {
        if (!selectedDay) return;
        const newPlan = [...profile.weeklyPlan];
        newPlan[selectedDay.index] = {
            ...newPlan[selectedDay.index],
            type: 'Rest',
            focus: 'Recovery',
            isLocked: true, // Lock it so it doesn't auto-regen
            drill_id: null
        };
        await updatePlan(newPlan);
    };

    // Level 3: Smart Swap
    const handleSmartSwap = (drillId, constraint) => {
        const originalDrill = dayDrills.find(d => d.drill_id === drillId);
        if (!originalDrill) return;

        const altDrill = getAlternativeDrill(originalDrill, constraint);
        if (altDrill) {
            const updatedDrills = dayDrills.map(d => d.drill_id === drillId ? altDrill : d);
            setDayDrills(updatedDrills);
            // Note: In a real app we would persist this swap to the Day Plan too
        } else {
            info("No suitable alternative found in database.");
        }
    };

    const updatePlan = async (newPlan) => {
        try {
            // Organized structure: Users/{userId}/Training/profile
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            await updateDoc(trainingProfileRef, { weeklyPlan: newPlan });
            user.profile.weeklyPlan = newPlan;
            setSelectedDay(null);
        } catch (error) {
            logger.error('Dashboard', 'Error updating plan:', error);
        }
    };

    const handleRemoveDrill = async (drillId) => {
        // 1. Optimistic Update
        const updatedDrills = dayDrills.filter(d => d.drill_id !== drillId);
        setDayDrills(updatedDrills);

        // 2. Persist to Firestore
        if (!selectedDay) return;

        try {
            const newPlan = [...profile.weeklyPlan];
            const dayIndex = selectedDay.index;
            const currentDay = newPlan[dayIndex];
            let modified = false;

            // A. Check custom_drills
            if (currentDay.custom_drills?.includes(drillId)) {
                newPlan[dayIndex] = {
                    ...currentDay,
                    custom_drills: currentDay.custom_drills.filter(id => id !== drillId)
                };
                modified = true;
            }

            // B. Check main drill_id
            if (currentDay.drill_id === drillId) {
                newPlan[dayIndex] = { ...currentDay, drill_id: null };
                modified = true;
            }

            if (modified) {
                // Organized structure: Users/{userId}/Training/profile
                const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
                await updateDoc(trainingProfileRef, { weeklyPlan: newPlan });
                user.profile.weeklyPlan = newPlan;
            }
        } catch (error) {
            logger.error('Dashboard', 'Error removing drill:', error);
        }
    };

    const handleAddDrill = async (drill) => {
        // 1. Optimistic UI Update
        const enhancedDrill = { ...drill, isCustom: true };
        setDayDrills([...dayDrills, enhancedDrill]);
        setShowDrillLibrary(false);

        // 2. Persist to Firestore
        if (!selectedDay) return;

        try {
            const newPlan = [...profile.weeklyPlan];
            // Ensure we are working with the correct ref
            const dayIndex = selectedDay.index;
            const currentDay = newPlan[dayIndex];

            // Append to custom_drills array
            const currentCustoms = currentDay.custom_drills || [];
            newPlan[dayIndex] = {
                ...currentDay,
                custom_drills: [...currentCustoms, drill.drill_id]
            };

            // Organized structure: Users/{userId}/Training/profile
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            await updateDoc(trainingProfileRef, { weeklyPlan: newPlan });

            // Update local ref so next time we open this day, it has the new data
            user.profile.weeklyPlan = newPlan;
        } catch (error) {
            logger.error('Dashboard', 'Error persisting custom drill:', error);
            // Revert UI if needed, but for now just logging
        }
    };

    const handleSwapDrill = async (newDrill) => {
        if (!selectedDay) return;

        const newPlan = [...profile.weeklyPlan];
        newPlan[selectedDay.index] = {
            ...newPlan[selectedDay.index],
            drill_id: newDrill.drill_id,
            focus: newDrill.name, // Update focus name for UI
            type: 'Custom' // Mark as custom
        };

        try {
            // Organized structure: Users/{userId}/Training/profile
            const trainingProfileRef = doc(db, FIRESTORE_PATHS.USERS, user.uid, FIRESTORE_PATHS.TRAINING_PROFILE.BASE, FIRESTORE_PATHS.TRAINING_PROFILE.CURRENT);
            await updateDoc(trainingProfileRef, {
                weeklyPlan: newPlan
            });
            // Optimistic update
            user.profile.weeklyPlan = newPlan;
            setSelectedDay(null); // Close modal
        } catch (error) {
            logger.error('Dashboard', 'Error swapping drill:', error);
        }
    };

    const closeModal = () => setSelectedDay(null);

    return (
        <div className="h-full bg-black text-white flex flex-col overflow-hidden">

            {/* Header */}
            <div className="p-6 pb-2">
                <h1 className="text-2xl font-bold">Welcome, {user?.displayName?.split(' ')[0] || 'Player'}.</h1>
                <p className="text-gray-400 text-sm">Let's keep the streak alive.</p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">

                {/* The Grind Score Card */}
                <div className="bg-gray-900 rounded-2xl border border-gray-800 p-4 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-4">
                        <div className="flex items-center gap-1 text-yellow-500 font-bold bg-yellow-500/10 px-2 py-1 rounded text-xs">
                            <Zap size={12} fill="currentColor" /> {totalXP} XP
                        </div>
                    </div>

                    <div className="mb-4">
                        <p className="text-gray-400 text-xs uppercase tracking-wider font-bold">Grind Score</p>
                        <h2 className="text-5xl font-bold text-white mt-1">{grindScore}</h2>
                        <p className="text-green-500 text-sm flex items-center mt-1">
                            <TrendingUp size={14} className="mr-1" /> Top 10% of players
                        </p>
                    </div>

                    <div className="h-16 w-full -mx-2">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                </div>

                {/* Player Card (Mini) */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-4 border border-gray-700 flex items-center gap-4">
                    <div className="w-16 h-20 bg-gray-700 rounded-lg border-2 border-gray-600 flex items-center justify-center relative">
                        <div className="absolute -top-2 -left-2 bg-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-[10px] font-bold">
                            {Math.floor(grindScore / 10 + 40)}
                        </div>
                        <Award className="text-gray-500" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-bold text-lg">{profile.role || 'Unranked'}</h3>
                        <p className="text-xs text-gray-400">{profile.battingStyle || 'Unknown Style'}</p>
                        <div className="flex gap-3 mt-2 text-xs">
                            <div>
                                <span className="block text-gray-500">MTS</span>
                                <span className="font-bold">{stats.matchesPlayed}</span>
                            </div>
                            <div>
                                <span className="block text-gray-500">AVG</span>
                                <span className="font-bold">-</span>
                            </div>
                            <div>
                                <span className="block text-gray-500">SR</span>
                                <span className="font-bold">-</span>
                            </div>
                        </div>
                    </div>
                    <ChevronRight className="text-gray-600" />
                </div>

                {/* Weekly Planner Skeleton */}
                <div className="space-y-4">
                    <div className="flex justify-between items-end px-1">
                        <h3 className="font-bold text-lg text-white">This Week</h3>
                        <span className="text-xs text-purple-400 font-medium">In-Season Phase</span>
                    </div>

                    <div className="space-y-4">
                        {(profile.weeklyPlan || []).map((day, index) => {
                            const isToday = index === (new Date().getDay() + 6) % 7;
                            return (
                                <div
                                    key={day.day}
                                    onClick={() => handleDayClick(day, index)}
                                    className={`relative p-0 rounded-xl border overflow-hidden cursor-pointer transition-all hover:border-gray-500 group
                                        ${isToday ? 'bg-gray-800 border-purple-500 shadow-lg shadow-purple-900/20' : 'bg-gray-800/40 border-gray-700'}`}
                                >
                                    {/* Status Indicator Strip (Left) */}
                                    <div className={`absolute top-0 bottom-0 left-0 w-1.5 
                                        ${isToday ? 'bg-purple-500 animate-pulse' :
                                            day.isLocked ? 'bg-gray-600' : 'bg-blue-500'}`}
                                    />

                                    <div className="p-4 pl-6 flex justify-between items-center">
                                        <div className="flex-1">
                                            {/* Header Section */}
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`text-xs font-bold tracking-wider uppercase ${isToday ? 'text-purple-400' : 'text-gray-500'}`}>
                                                    {day.day}
                                                </span>
                                                {/* Smart Badges */}
                                                {day.isInjected && (
                                                    <span className="flex items-center gap-1 text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded font-bold border border-yellow-500/20">
                                                        <Sparkles size={10} className="fill-current" /> Smart Plan
                                                    </span>
                                                )}
                                                {day.type === 'Custom' && (
                                                    <span className="flex items-center gap-1 text-[10px] bg-blue-500/10 text-blue-500 px-1.5 py-0.5 rounded font-bold border border-blue-500/20">
                                                        Custom
                                                    </span>
                                                )}
                                            </div>

                                            {/* Main Title & Subtitle */}
                                            <h4 className={`font-bold text-base ${isToday ? 'text-white' : 'text-gray-300'}`}>
                                                {day.type === 'Rest' ? 'Rest & Recovery' : day.type + ' Session'}
                                            </h4>
                                            <p className="text-xs text-gray-400 mt-0.5 max-w-[200px] truncate">
                                                Focus: {day.focus}
                                            </p>

                                            {/* Workload Battery & Time */}
                                            <div className="flex items-center gap-4 mt-3">
                                                <div className="flex items-center gap-1.5">
                                                    <div className="flex gap-0.5">
                                                        {[1, 2, 3].map(bar => (
                                                            <div key={bar} className={`w-1 h-3 rounded-sm ${(day.type === 'Volume' || day.type === 'Match Day') && bar <= 3 ? 'bg-green-500' :
                                                                (day.type === 'Technical') && bar <= 1 ? 'bg-blue-500' :
                                                                    'bg-gray-700'
                                                                }`} />
                                                        ))}
                                                    </div>
                                                    <span className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">
                                                        {day.type === 'Rest' ? 'Low' : day.type === 'Volume' ? 'High' : 'Med'} Load
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                                    <div className="w-1 h-1 rounded-full bg-gray-600" />
                                                    45 Mins
                                                </div>
                                                {/* XP Pill */}
                                                {!isToday && !day.isLocked && (day.type !== 'Rest') && (
                                                    <div className="flex items-center gap-1 text-[10px] text-yellow-500 font-bold bg-yellow-500/10 px-1.5 py-0.5 rounded-full ml-auto">
                                                        +120 XP
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Icon */}
                                        <div className="pl-4">
                                            {isToday ? (
                                                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center shadow-lg shadow-purple-600/30">
                                                    <ChevronRight size={18} className="text-white" />
                                                </div>
                                            ) : (
                                                <ChevronRight size={18} className="text-gray-600 group-hover:text-gray-400 transition-colors" />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>

            {/* Quick Actions (Bottom Dock) */}
            <div className="p-6 pt-2 bg-gradient-to-t from-black to-transparent">
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => onLogActivity('match')}
                        className="bg-gray-800 p-4 rounded-xl flex flex-col items-center gap-2 hover:bg-gray-700 transition"
                    >
                        <Target className="text-blue-400" />
                        <span className="text-xs font-bold text-gray-300">Log Match</span>
                    </button>
                    <button
                        onClick={() => onLogActivity('training')}
                        className="bg-gray-800 p-4 rounded-xl flex flex-col items-center gap-2 hover:bg-gray-700 transition"
                    >
                        <Plus className="text-green-400" />
                        <span className="text-xs font-bold text-gray-300">Log Training</span>
                    </button>
                </div>
            </div>

            {/* Day Detail Modal */}
            {
                selectedDay && (
                    <div className="fixed inset-0 bg-black/90 flex items-end sm:items-center justify-center z-50 animate-fadeIn">
                        <div className="bg-gray-900 border-t sm:border border-gray-800 w-full sm:w-96 rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-hidden flex flex-col relative">

                            {/* Modal Header */}
                            <div className="p-6 pb-4 border-b border-gray-800 flex justify-between items-center bg-gray-900 z-10">
                                <div>
                                    <h2 className="text-2xl font-bold text-white">{selectedDay.day}</h2>
                                    <p className="text-purple-400 text-sm font-medium">{selectedDay.type} Focus</p>
                                </div>
                                <button onClick={closeModal} className="p-2 bg-gray-800 rounded-full text-gray-400 hover:text-white transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Scrollable Content */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-8 pb-32">

                                {/* Smart Insight Banner */}
                                <div className="bg-gradient-to-br from-indigo-900/50 to-purple-900/50 p-4 rounded-xl border border-indigo-500/30 flex gap-4">
                                    <div className="mt-1 bg-indigo-500/20 p-2 rounded-lg h-fit">
                                        <Zap className="text-indigo-400" size={18} fill="currentColor" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-white mb-1">Session Insight</h4>
                                        <p className="text-xs text-gray-300 leading-relaxed">
                                            {selectedDay.isInjected
                                                ? "Based on your recent match data, we've injected specific corrective drills to target your dismissal patterns."
                                                : "This session is designed to maintain your technical foundation and prepare you for high-volume work later in the week."}
                                        </p>
                                    </div>
                                </div>

                                {/* Warm Up Block */}
                                <div>
                                    <h3 className="text-gray-500 text-xs uppercase font-bold mb-3 flex items-center gap-2">
                                        <span>🔥</span> Warm Up (10 Mins)
                                    </h3>
                                    <div className="bg-gray-800/40 p-3 rounded-xl border border-gray-800 space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-700/50 flex items-center justify-center text-gray-400 text-xs font-bold">1</div>
                                            <div className="flex-1">
                                                <p className="text-sm text-gray-300 font-medium">Dynamic Mobility Flow</p>
                                                <p className="text-[10px] text-gray-500">5 Mins • General Prep</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-700/50 flex items-center justify-center text-gray-400 text-xs font-bold">2</div>
                                            <div className="flex-1">
                                                <p className="text-sm text-gray-300 font-medium">Reaction Sprints</p>
                                                <p className="text-[10px] text-gray-500">5 Mins • Activation</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Core Work Block */}
                                <div>
                                    <div className="flex justify-between items-end mb-3">
                                        <h3 className="text-purple-400 text-xs uppercase font-bold flex items-center gap-2">
                                            <span>⚡</span> Core Work
                                        </h3>
                                        {dayDrills.length > 0 && <span className="text-[10px] bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full">{dayDrills.length} Drills</span>}
                                    </div>

                                    {dayDrills.length > 0 ? (
                                        <div className="space-y-4">
                                            {dayDrills.map((drill, i) => (
                                                <div key={drill.drill_id} className="bg-gray-800 p-5 rounded-xl border border-gray-700 relative overflow-hidden group">
                                                    <div className="absolute top-0 left-0 w-1 h-full bg-purple-500" />

                                                    <div className="flex justify-between items-start mb-2 pl-2">
                                                        <div className="flex items-center gap-3">
                                                            {/* Media Placeholder */}
                                                            <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center shrink-0">
                                                                <PlayCircle size={20} className="text-gray-400" />
                                                            </div>
                                                            <div>
                                                                <h4 className="font-bold text-white text-lg leading-tight">{drill.name}</h4>
                                                                {/* Volume Target Badge */}
                                                                <span className="inline-block mt-1 text-[10px] font-bold text-gray-300 bg-gray-700/50 px-2 py-0.5 rounded">
                                                                    {adjustDrillIntensity(drill.recommended_volume || "3 Sets / 10 Reps", intensity)}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Drill Actions Menu */}
                                                        <div className="relative group/menu">
                                                            <button className="p-1.5 text-gray-500 hover:text-white rounded-full hover:bg-gray-700 transition">
                                                                <MoreVertical size={16} />
                                                            </button>
                                                            {/* Dropdown (CSS-based hover for simplicity, or click state in real app) */}
                                                            <div className="absolute right-0 top-8 w-32 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 hidden group-hover/menu:block">
                                                                <button
                                                                    onClick={() => handleSmartSwap(drill.drill_id, 'No Partner')}
                                                                    className="w-full text-left px-3 py-2 text-xs text-gray-300 hover:bg-gray-800 flex items-center gap-2"
                                                                >
                                                                    <RefreshCw size={12} /> Swap
                                                                </button>
                                                                <button
                                                                    onClick={() => handleRemoveDrill(drill.drill_id)}
                                                                    className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-gray-800 flex items-center gap-2"
                                                                >
                                                                    <Trash2 size={12} /> Remove
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Setup Instructions */}
                                                    {
                                                        drill.setup_instructions && (
                                                            <div className="mb-3 bg-gray-900/50 p-3 rounded-lg ml-2">
                                                                <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Setup</p>
                                                                <p className="text-xs text-gray-300">{drill.setup_instructions}</p>
                                                            </div>
                                                        )
                                                    }

                                                    {/* Execution Steps */}
                                                    {
                                                        drill.execution_steps && (
                                                            <div className="mb-3 ml-2">
                                                                <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Execution</p>
                                                                <ul className="space-y-1">
                                                                    {drill.execution_steps.map((step, idx) => (
                                                                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                                                                            <span className="text-purple-500 mt-0.5">•</span>
                                                                            <span>{step}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        )
                                                    }

                                                    {/* Constraints / Tags */}
                                                    < div className="flex gap-2 mt-3 flex-wrap ml-2" >
                                                        {
                                                            drill.constraints?.min_players > 1 && (
                                                                <span className="text-[10px] bg-blue-900/30 text-blue-400 px-2 py-1 rounded border border-blue-800">
                                                                    👥 Partner
                                                                </span>
                                                            )
                                                        }
                                                        {
                                                            drill.constraints?.space_req === 'Net' && (
                                                                <span className="text-[10px] bg-green-900/30 text-green-400 px-2 py-1 rounded border border-green-800">
                                                                    🏏 Net
                                                                </span>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            ))}


                                        </div>
                                    ) : (
                                        // Empty State but allows adding
                                        <div className="space-y-4">
                                            <div className="p-6 text-center border border-dashed border-gray-700 rounded-xl">
                                                <p className="text-gray-500 italic text-sm">No drills scheduled.</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Add Drill Button - ALWAYS VISIBLE */}
                                    <div className="mt-4">
                                        <button
                                            onClick={() => setShowDrillLibrary(true)}
                                            className="w-full py-3 rounded-xl border border-dashed border-gray-600/50 text-gray-500 hover:text-white hover:border-gray-500 hover:bg-gray-800/20 transition-all flex items-center justify-center gap-2 group"
                                        >
                                            <div className="bg-gray-800 rounded-full p-1 group-hover:bg-gray-700 transition">
                                                <Plus size={16} />
                                            </div>
                                            <span className="text-xs font-bold uppercase tracking-wider">Add Drill</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Cool Down Block */}
                                <div>
                                    <h3 className="text-blue-400 text-xs uppercase font-bold mb-3 flex items-center gap-2">
                                        <span>🧊</span> Cool Down (5 Mins)
                                    </h3>
                                    <div className="bg-gray-800/40 p-3 rounded-xl border border-gray-800">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-gray-700/50 flex items-center justify-center text-gray-400 text-xs font-bold">1</div>
                                            <div className="flex-1">
                                                <p className="text-sm text-gray-300 font-medium">Static Stretching</p>
                                                <p className="text-[10px] text-gray-500">5 Mins • Recovery</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>



                            </div>

                            {/* Sticky Footer CTA */}
                            <div className="p-4 bg-gray-900 border-t border-gray-800 z-20">
                                <button onClick={() => onStartSession && onStartSession(selectedDay, dayDrills)} className="w-full bg-green-500 hover:bg-green-600 text-black font-bold py-3.5 rounded-xl text-lg shadow-lg shadow-green-900/20 transition-all flex items-center justify-center gap-2">
                                    START SESSION <ChevronRight size={20} strokeWidth={3} />
                                </button>
                            </div>

                            {/* Drill Library Modal Layer */}
                            {showDrillLibrary && (
                                <div className="absolute inset-0 bg-gray-900 z-50 flex flex-col animate-slideUp">
                                    <div className="p-4 border-b border-gray-800 flex justify-between items-center bg-gray-900 shrink-0">
                                        <h3 className="text-lg font-bold text-white">Drill Library</h3>
                                        <button onClick={() => setShowDrillLibrary(false)} className="p-2 bg-gray-800 rounded-full text-gray-400">
                                            <X size={20} />
                                        </button>
                                    </div>

                                    {/* Library Interaction Area */}
                                    <DrillLibraryView
                                        onAddDrill={handleAddDrill}
                                        onCreateDrill={() => setShowDrillCreator(true)}
                                        mergedDrills={mergedDrills}
                                    />

                                    {/* Custom Drill Creator Layer */}
                                    {showDrillCreator && (
                                        <DrillCreator
                                            userId={user.uid}
                                            onCancel={() => setShowDrillCreator(false)}
                                            onComplete={(newDrill) => {
                                                setShowDrillCreator(false);
                                                // It will be added to the library via the onSnapshot listener
                                            }}
                                        />
                                    )}
                                </div>
                            )}


                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default Dashboard;
